import type { ReactNode, SVGProps } from 'react'

/**
 * The app's icons, drawn on one 24px grid with one stroke weight so they sit
 * together. Hand-written rather than a package: there are only a couple of dozen.
 */
type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'> & { size?: number }

function Svg({ size = 22, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  )
}

export const PlusIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
)

export const BackIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M15 5l-7 7 7 7" />
  </Svg>
)

export const ChevronIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M7 10l5 5 5-5" />
  </Svg>
)

export const SearchIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="M16 16l4 4" />
  </Svg>
)

/** Pipeline: three stacked rows, the list itself. */
export const PipelineIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="5.5" cy="6.5" r="1.5" />
    <circle cx="5.5" cy="12" r="1.5" />
    <circle cx="5.5" cy="17.5" r="1.5" />
    <path d="M10 6.5h9.5M10 12h9.5M10 17.5h6" />
  </Svg>
)

/** Film: a clapperboard. */
export const MoreIcon = (props: IconProps) => (
  <Svg {...props} strokeWidth={2.4}>
    <path d="M6 12h.01M12 12h.01M18 12h.01" />
  </Svg>
)

export const CheckIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Svg>
)

export const CloseIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Svg>
)

export const SunIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v1.5M12 19.5V21M3 12h1.5M19.5 12H21M5.6 5.6l1.1 1.1M17.3 17.3l1.1 1.1M5.6 18.4l1.1-1.1M17.3 6.7l1.1-1.1" />
  </Svg>
)

export const MoonIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M19.5 14.5A7.5 7.5 0 0 1 9.5 4.5a7.5 7.5 0 1 0 10 10z" />
  </Svg>
)

export const TrashIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4.5 7h15M10 4h4M6.5 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4l.8-12" />
  </Svg>
)

export const LinkIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M14 5h5v5M19 5l-8 8M17 14v4a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 5 18V8.5A1.5 1.5 0 0 1 6.5 7H10" />
  </Svg>
)

export const ArrowUpIcon = (props: IconProps) => (
  <Svg {...props} strokeWidth={2.2}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </Svg>
)

export const CopyIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
    <path d="M15.5 8.5V6a1.5 1.5 0 0 0-1.5-1.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h2.5" />
  </Svg>
)

export const KillIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="8" />
    <path d="M9 9l6 6M15 9l-6 6" />
  </Svg>
)

/** Shorts: a phone-shaped frame with a play mark. */
export const VerticalVideoIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="6.5" y="3" width="11" height="18" rx="2.5" />
    <path d="M10.6 9.8v4.4l3.6-2.2z" />
  </Svg>
)

/** YouTube: a 16:9 frame with a play mark. Not the logo, which is theirs. */
export const WideVideoIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
    <path d="M10.4 9.8v4.4l3.6-2.2z" />
  </Svg>
)

/** LinkedIn: a pen, because that channel is written, not filmed. */
export const PenIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4.5 19.5l1-4.2L15.8 5a2.1 2.1 0 0 1 3 3L8.7 18.5z" />
    <path d="M14 7l3 3M4.5 19.5h15" />
  </Svg>
)

/** What is on screen during a beat of the script. */
export const ScreenIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="18" height="13" rx="2.5" />
    <path d="M9 20.5h6M12 17v3.5" />
  </Svg>
)

/** Fold the idea panel away to the right edge. */
export const PanelCloseIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M15 4v16M8.5 9.5 11 12l-2.5 2.5" />
  </Svg>
)

/** Unfold it again. */
export const PanelOpenIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M15 4v16M11 9.5 8.5 12l2.5 2.5" />
  </Svg>
)

/** Show the whole canvas. */
export const FitIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M8 3.5H5.5a2 2 0 0 0-2 2V8M20.5 8V5.5a2 2 0 0 0-2-2H16M3.5 16v2.5a2 2 0 0 0 2 2H8M16 20.5h2.5a2 2 0 0 0 2-2V16" />
  </Svg>
)

export const MinusIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M5 12h14" />
  </Svg>
)

/** Drag to reorder. */
export const GripIcon = (props: IconProps) => (
  <Svg {...props} strokeWidth={2.6}>
    <path d="M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01" />
  </Svg>
)

/** Still being edited: the video is on its way. */
export const ClockIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 1.8" />
  </Svg>
)

export const PlayIcon = (props: IconProps) => (
  <Svg {...props} fill="currentColor" stroke="none">
    <path d="M8 5.5v13l10.5-6.5z" />
  </Svg>
)
